/**
 * Normalizes a filename using NFKC normalization and truncates it to a maximum length.
 * Preserves the file extension when truncating.
 *
 * @param filename - The original filename
 * @param maxLength - Maximum length for the filename (default: 100)
 * @returns Normalized and truncated filename
 */
export function normalizeFilename(
  filename: string,
  maxLength: number = 100,
): string {
  // Apply NFKC normalization
  const normalized = filename.normalize("NFKC");

  // If the filename is already within the limit, return it
  if (normalized.length <= maxLength) {
    return normalized;
  }

  // Extract file extension
  const lastDotIndex = normalized.lastIndexOf(".");
  const hasExtension = lastDotIndex > 0 && lastDotIndex < normalized.length - 1;

  if (!hasExtension) {
    // No extension, just truncate
    return normalized.slice(0, maxLength);
  }

  const extension = normalized.slice(lastDotIndex);
  const nameWithoutExtension = normalized.slice(0, lastDotIndex);

  // Reserve space for the extension
  const maxNameLength = maxLength - extension.length;

  if (maxNameLength <= 0) {
    // Extension itself is longer than maxLength, return just the extension truncated
    return extension.slice(0, maxLength);
  }

  // Truncate the name part and append the extension
  return nameWithoutExtension.slice(0, maxNameLength) + extension;
}
